import {AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Optional, Output, ViewChild} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { CustomValidators } from 'src/app/shared/validators/CustomValidators';
import { AuthService } from '../../../auth/services/auth.service';
import { AlertMessageService } from 'src/app/modules/shared/services/alert-message.service';
import { first } from 'rxjs';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormHelperService } from 'src/app/modules/shared/services/form-helper.service';
import { TranslateService } from '@ngx-translate/core';
import {Router} from "@angular/router";

const COMMON_PASSWORDS = ['123456789', 'password', 'qwerty', '12345678'];

@Component({
  selector: 'app-change-password2',
  templateUrl: './change-password2.component.html',
  styleUrls: ['./change-password2.component.scss']
})
export class ChangePassword2Component implements OnInit, AfterViewInit {
  @Input() presentation: 'page' | 'modal' = 'modal';

  @ViewChild('currentPasswordInput') currentPasswordInput?: ElementRef<HTMLInputElement>;

  @Output() completed = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  form!: FormGroup;
  loading = false;

  showCurrent = false;
  showNew = false;
  showConfirm = false;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    public translate: TranslateService,
    private alertMessage: AlertMessageService,
    public formHelper: FormHelperService,
    private router: Router,
    @Optional() public modal: NgbActiveModal
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      currentPassword: [null, [Validators.required, Validators.minLength(8), Validators.maxLength(80)]],
      newPassword: [null, [
        Validators.required,
        Validators.minLength(9),
        Validators.maxLength(80),
        this.commonPasswordValidator(),
        this.numericPasswordValidator()
      ]],
      confirmPassword: [null, [Validators.required]]
    }, {
      validators: CustomValidators.passwordMatchValidator('newPassword', 'confirmPassword')
    });
  }

  ngAfterViewInit(): void {
    // En modal el focus lo pone ng-bootstrap vía el atributo ngbAutofocus del
    // input; este focus directo solo hace falta en la presentación como página.
    if (this.presentation === 'page') {
      this.currentPasswordInput?.nativeElement.focus();
    }
  }

  // Estado en vivo para el checklist de requisitos (refleja los validadores del form)
  get requirements(): { label: string; ok: boolean }[] {
    const value: string = this.form?.get('newPassword')?.value || '';
    const confirm: string = this.form?.get('confirmPassword')?.value || '';
    return [
      { label: 'At least 9 characters', ok: value.length >= 9 },
      { label: 'Not only numbers', ok: !!value && !/^\d+$/.test(value) },
      { label: 'Not a commonly used password', ok: !!value && !COMMON_PASSWORDS.includes(value) },
      { label: 'Both passwords match', ok: !!value && value === confirm },
    ];
  }

  private commonPasswordValidator(): ValidatorFn {
    return (control: AbstractControl) =>
      COMMON_PASSWORDS.includes(control.value) ? { commonPassword: true } : null;
  }

  private numericPasswordValidator(): ValidatorFn {
    return (control: AbstractControl) =>
      (/^\d+$/.test(control.value) ? { numericPassword: true } : null);
  }

  toggle(field: 'current' | 'new' | 'confirm') {
    if (field === 'current') this.showCurrent = !this.showCurrent;
    if (field === 'new') this.showNew = !this.showNew;
    if (field === 'confirm') this.showConfirm = !this.showConfirm;
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const { currentPassword, newPassword, confirmPassword } = this.form.value;
    this.loading = true;

    this.authService.changePassword(newPassword, confirmPassword, currentPassword)
      .pipe(first())
      .subscribe({
        next: () => {
          this.alertMessage.showMessage(
            this.translate.instant('GENERAL.SUCCESS'),
            this.translate.instant('AUTH.PASSWORD_CHANGED_SUCCESS'),
            'success',
            this.translate.instant('GENERAL.OK')
          ).then(() => {
            this.completed.emit();
            if (this.presentation === 'page') this.router.navigate(['/dashboard']);
            if (this.presentation === 'modal' && this.modal) this.modal.close(true);
          });
          this.loading = false;
        },
        error: (error) => {
          this.loading = false;
          const msg = this.getError(error, this.translate.instant('AUTH.PASSWORD_CHANGE_ERROR'));
          this.alertMessage.showMessage(this.translate.instant('GENERAL.ERROR'), msg, 'error', this.translate.instant('GENERAL.OK'));
        }
      });
  }

  cancel(): void {
    if (this.presentation === 'page') this.router.navigate(['/auth/login']);
    else {
      this.cancelled.emit();
      if (this.modal) this.modal.dismiss();
    }
  }

  private getError(error: any, fallback: string): string {
    if (error?.error?.non_field_errors?.[0]) return error.error.non_field_errors[0];
    if (error?.error?.message) return error.error.message;
    if (error?.error?.error) return error.error.error;
    if (error?.message) return error.message;
    return fallback;
  }

  get f() { return this.form.controls; }
}
